import { apiGet, apiPost } from './apiUtils';
import { getCachedApiUrl } from './apiConfig';
import type { AdminTariff, BillingPeriod, SubscriptionDetailsResponse } from './adminApiUtils';

export type { AdminTariff, BillingPeriod, SubscriptionDetailsResponse };

/** Активная подписка текущего пользователя или null (404). */
export const getActiveSubscriptionOrNull = async (): Promise<SubscriptionDetailsResponse | null> => {
  const response = await apiGet('/subscriptions/active-subscription');
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Ошибка загрузки подписки');
  }
  const data = await response.json();
  if (data.subscription) {
    return data as SubscriptionDetailsResponse;
  }
  if (data.id && data.user_id) {
    return { subscription: data, payments: [] };
  }
  return data;
};

export const getActiveTariffs = async (): Promise<AdminTariff[]> => {
  const response = await apiGet('/subscriptions/active-tariffs');
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Ошибка загрузки тарифов');
  }
  return response.json();
};

/**
 * POST /subscriptions/start-payment (Bearer).
 * success_url / fail_url — полные https URL; хост из разрешённых для клиента или как у API.
 * success: либо база без плейсхолдера (бэкенд допишет /{order_id}), либо в строке есть {order_id} для подстановки.
 */
export interface StartPaymentPayload {
  tariff_id: number;
  billing_period: BillingPeriod;
  success_url: string;
  fail_url: string;
  /** По умолчанию false. Если true — после успешной оплаты баланс протоколов заменяется на начисление за период, без суммирования со старым остатком. */
  reset?: boolean;
}

/** База success URL без order_id: бэкенд добавит /{order_id} к этому URL. */
export const buildSubscriptionPaymentSuccessUrlBase = (origin: string): string =>
  `${origin.replace(/\/$/, '')}/subscription/payment/success`;

export const buildSubscriptionPaymentFailUrl = (origin: string): string =>
  `${origin.replace(/\/$/, '')}/subscription/payment/fail`;

export interface StartPaymentResponse {
  payment_url: string;
  subscription_id: number;
}

export const startSubscriptionPayment = async (
  payload: StartPaymentPayload
): Promise<StartPaymentResponse> => {
  const response = await apiPost('/subscriptions/start-payment', payload);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || errorData.message || `Ошибка создания платежа (${response.status})`
    );
  }
  return response.json();
};

/** GET /subscriptions/confirm-payment/{order_id} — 200, в т.ч. при повторном заходе (оплата уже обработана). */
export interface ConfirmPaymentSuccessBody {
  subscription_id: number;
  detail?: string;
}

/**
 * GET /subscriptions/confirm-payment/{order_id} — без Bearer (редирект с банка).
 * 402 — банк ещё не подтвердил. 429 — лимит запросов; на фронте ждём и повторяем (как pending).
 */
export const confirmPaymentByOrderId = async (
  orderId: string,
  init?: RequestInit
): Promise<ConfirmPaymentSuccessBody | 'pending'> => {
  const base = getCachedApiUrl().replace(/\/$/, '');
  const url = `${base}/subscriptions/confirm-payment/${encodeURIComponent(orderId)}`;
  const response = await fetch(url, { method: 'GET', ...init });
  if (response.status === 200) {
    return response.json() as Promise<ConfirmPaymentSuccessBody>;
  }
  if (response.status === 402 || response.status === 429) {
    return 'pending';
  }
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.detail || errorData.message || 'Не удалось подтвердить оплату');
};

/** Прямой сброс лимита (например, из админки). Для пользователя — через start-payment с `reset: true`. */
export const resetSubscriptionLimits = async (): Promise<SubscriptionDetailsResponse> => {
  const response = await apiPost('/subscriptions/reset-limits', {});
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Ошибка сброса лимита');
  }
  return response.json();
};

export const resetSubscriptionRebill = async (): Promise<void> => {
  const response = await apiPost('/subscriptions/reset-rebill', {});
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Ошибка отмены автопродления');
  }
};

export const changeSubscriptionTariff = async (tariffId: number): Promise<SubscriptionDetailsResponse> => {
  const response = await apiPost('/subscriptions/change-tariff', { tariff_id: tariffId });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Ошибка смены тарифа');
  }
  return response.json();
};
